import { ReactNode, useEffect } from 'react';
import { DoubleCheckLockerProps } from './DoubleCheckLocker.types';
import { useLocalization } from '../../../context/Localization/localization';
import { GoMotoComponent, GoMotoFunction } from '../../../API/GoMoto.types';
import { ScrollableBody } from '../../../components/Layout/ScrollableBody';
import { useSetKioskBannerVisibility, useSetHeaderProps } from '../../../context/Page/page';
import { CTAButton } from '../../ButtonBase';
import { GMButtonGroup } from '../../ButtonGroup';
import { Heading } from '../../Heading/Heading';
import { GMRoundedIcon, GMSizedIcon } from '../../Icon';
import { IAppointment } from '../../../API/GoMoto';
import { useAppointmentState, useTowInProcess } from '../../../context/Appointment/appointment';
import { AppointmentLoanerOrRentalOption } from '../../../API/Enums';

/**
 * Asks the user to make sure that they correctly retrieved their key or exchanged keys at the locker.
 * They can re-open the locker if they need to, otherwise they can confirm they correctly finished picking up
 * their vehicle.
 *
 * @param lockerOperationType - {@link DoubleCheckLockerProps.appointment appointment} description.
 * @param onReopenLocker - {@link DoubleCheckLockerProps.onReopenLocker onReopenLocker} description.
 * @param onContinue - {@link DoubleCheckLockerProps.onContinue onContinue} description.
 */
const DoubleCheckLocker: GoMotoComponent<DoubleCheckLockerProps> = ({
    lockerOperationType,
    onReopenLocker,
    onContinue,
}) => {
    const Localization = useLocalization();
    const showBanner = useSetKioskBannerVisibility();
    const setHeader = useSetHeaderProps();
    const appointment: IAppointment = useAppointmentState();
    const towInProcess = useTowInProcess();

    useEffect(() => {
        const timer: NodeJS.Timeout = setTimeout(() => {
            onContinue();
        }, 60000);

        return () => {
            clearTimeout(timer);
        };
    }, [onContinue]);

    useEffect(() => {
        showBanner(true);
        setHeader(undefined);
    }, [setHeader, showBanner]);

    const getSecondaryHeading: GoMotoFunction<void, ReactNode> = () => {
        switch (lockerOperationType) {
            case 'drop-off':
                return (
                    towInProcess ? (
                        <>
                            <p>
                                {Localization.DidYouPutVehicleKeysInTowIn}
                            </p>
                        </>
                    ) : (
                        <>
                            <p>
                                {Localization.HangOntoYourHouseKeys}
                            </p>
                            <p>
                                {Localization.DidYouPutVehicleKeysIn}
                            </p>
                        </>
                    )
                );
            case 'pick-up':
                switch (appointment.returningLoanerOrRental) {
                    case AppointmentLoanerOrRentalOption.Loaner:
                        return (
                            <>
                                {Localization.formatString(
                                    Localization.DidYouReturnTheKey,
                                    <b>{Localization.Loaner.toLocaleLowerCase()}</b>,
                                )}
                            </>
                        );
                    case AppointmentLoanerOrRentalOption.Rental:
                        return (
                            <>
                                {Localization.formatString(
                                    Localization.DidYouReturnTheKey,
                                    <b>{Localization.Rental.toLocaleLowerCase()}</b>,
                                )}
                            </>
                        );
                    default:
                        return Localization.DidYouRetrieveYourKeys;
                }
            default:
                return Localization.AnErrorHasOccured;
        }
    };

    return (
        <ScrollableBody
            heading={
                <Heading
                    primaryHeading={Localization.DoubleChecking}
                    secondaryHeading={getSecondaryHeading()}
                    roundedIcon={
                        <GMRoundedIcon
                            roundedBackgroundSize={'large'}
                            themeType={'secondary'}
                        >
                            <GMSizedIcon
                                iconType={'Key'}
                                iconSize={'extraLarge'}
                            />
                        </GMRoundedIcon>
                    }
                    showMustache
                />
            }
            proceedButtons={
                <GMButtonGroup
                    alignment={'center'}
                    buttons={[
                        (
                            <CTAButton
                                styleType={'primary'}
                                label={Localization.YesContinue}
                                onClick={onContinue}
                                kioskOptions={{ square: true }}
                                id={Localization.YesContinue}
                                ariaLabel={Localization.YesContinue}
                            />
                        ),
                        (
                            <CTAButton
                                styleType={'secondary'}
                                label={Localization.NoReopenLocker}
                                onClick={onReopenLocker}
                                kioskOptions={{ square: true }}
                                id={Localization.NoReopenLocker}
                                ariaLabel={Localization.NoReopenLocker}
                            />
                        ),
                    ]}
                />
            }
            unsetButtons
        />
    );
};

export default DoubleCheckLocker;
